import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { Users, BookOpen, Heart, MessageSquare, Calendar, ArrowRight, CheckCircle, Clock, MapPin, Loader2 } from 'lucide-react';
import { youthProgramInfo } from '../data/mock';
import { fetchEvents, submitYouthForm } from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

// Helper function to parse date string as local date (not UTC)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
  }
  return new Date(dateStr + 'T12:00:00');
};

const YouthProgramPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    email: '',
    phone: '',
    interests: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [youthEvents, setYouthEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    const loadYouthEvents = async () => {
      try {
        const events = await fetchEvents('youth');
        setYouthEvents(events);
      } catch (err) {
        logger.error('Failed to fetch youth events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadYouthEvents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitYouthForm({
        ...formData,
        age: parseInt(formData.age)
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', age: '', email: '', phone: '', interests: '' });
      }, 5000);
    } catch (err) {
      logger.error('Failed to submit youth registration:', err);
      alert('Failed to submit registration. Please try again.');
    }
  };

  const activityIcons = {
    'Weekly Discussion Groups': MessageSquare,
    'Educational Seminars': BookOpen,
    'Social Events': Users,
    'Volunteer Opportunities': Heart
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="w-24 h-24 mx-auto mb-8 border-2 border-[#c5a572] flex items-center justify-center">
              <Users className="w-12 h-12 text-[#c5a572]" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6">Fatimia Youth Group</h1>
            <div className="inline-block px-6 py-2 border border-[#c5a572] text-[#c5a572] uppercase tracking-[0.2em] text-sm mb-8">
              Ages {youthProgramInfo.ageRange}
            </div>
            <p className="text-white/70 text-lg">
              {youthProgramInfo.description}
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* About the Youth Group */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="card card-gold">
              <p className="text-[#5a6d62] text-lg leading-relaxed mb-6">
                {youthProgramInfo.additionalInfo}
              </p>
              <p className="text-[#5a6d62] text-lg leading-relaxed">
                {youthProgramInfo.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="section section-alt pattern-overlay">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">What We Offer</p>
            <h2 className="heading-2 mb-6">Our Activities</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>

          <div className="grid-2">
            {youthProgramInfo.activities.map((activity, index) => {
              const IconComponent = activityIcons[activity.title] || Users;
              return (
                <div key={`activity-${activity.title}`} className="card group">
                  <div className="flex items-start gap-5">
                    <div className="w-16 h-16 border border-[#c5a572]/30 group-hover:border-[#c5a572] group-hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-[#c5a572]" />
                    </div>
                    <div>
                      <h3 className="text-xl text-[#1a2f23] mb-2 group-hover:text-[#c5a572] transition-colors">{activity.title}</h3>
                      <p className="text-[#5a6d62]">{activity.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Benefits</p>
              <h2 className="heading-2 mb-6">Why Join Our Youth Group?</h2>
              <div className="islamic-divider">
                <div className="islamic-star" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Build lifelong friendships with fellow Muslim youth',
                'Deepen your understanding of faith and Islamic values',
                'Explore relevant topics that affect your daily life',
                'Give back to the community through volunteer work',
                'Participate in social events and gatherings',
                'Serve in the way of the Ahlul-bayt (as)'
              ].map((benefit, index) => (
                <div key={`benefit-${index}-${benefit.slice(0,20)}`} className="card group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-[#c5a572]/30 group-hover:border-[#c5a572] group-hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#c5a572]" />
                    </div>
                    <span className="text-[#1a2f23] group-hover:text-[#c5a572] transition-colors">{benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section className="section section-alt">
        <div className="container mx-auto px-4">
          <div className="grid-2 items-start">
            <div>
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Get Involved</p>
              <h2 className="heading-2 mb-6">Join the Youth Group</h2>
              <p className="text-[#5a6d62] mb-8 leading-relaxed">
                Open to young Muslims ages <strong>{youthProgramInfo.ageRange}</strong>. Whether you're interested in 
                discussion groups, seminars, social events, or volunteering, there's a place for you here.
              </p>

              <div className="p-8 bg-[#1a2f23] text-white">
                <h3 className="text-xl text-[#c5a572] mb-6 tracking-wide">Upcoming Youth Events</h3>
                {loadingEvents ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#c5a572] animate-spin" />
                  </div>
                ) : youthEvents.length > 0 ? (
                  <div className="space-y-5">
                    {youthEvents.slice(0, 5).map((event) => (
                      <div key={event.id} className="flex items-start gap-4">
                        <div className="w-12 h-12 border border-[#c5a572]/30 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-[#c5a572]" />
                        </div>
                        <div>
                          <p className="text-white tracking-wide font-medium">{event.title}</p>
                          <div className="flex items-center gap-3 text-white/60 text-sm tracking-wide mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {event.time}
                            </span>
                          </div>
                          <p className="text-white/50 text-sm mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Calendar className="w-10 h-10 text-[#c5a572]/50 mx-auto mb-3" />
                    <p className="text-white/60 text-sm">No upcoming youth events scheduled.</p>
                    <p className="text-white/40 text-xs mt-1">Check back soon for new programs!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="card card-gold">
              <h3 className="text-2xl text-[#1a2f23] mb-2">Register Now</h3>
              <p className="text-sm text-[#5a6d62] mb-6">Join our vibrant youth community today (Ages {youthProgramInfo.ageRange}).</p>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[#c5a572]" />
                  </div>
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Welcome to the Team!</h3>
                  <p className="text-[#5a6d62]">We'll be in touch with upcoming activities.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm uppercase tracking-wider text-[#5a6d62]">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="age" className="text-sm uppercase tracking-wider text-[#5a6d62]">Age *</Label>
                    <Input
                      id="age"
                      type="number"
                      min="14"
                      max="30"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="Your age (14-30)"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm uppercase tracking-wider text-[#5a6d62]">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm uppercase tracking-wider text-[#5a6d62]">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(604) 555-0123"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="interests" className="text-sm uppercase tracking-wider text-[#5a6d62]">What activities interest you?</Label>
                    <Textarea
                      id="interests"
                      value={formData.interests}
                      onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                      placeholder="Tell us what you'd like to participate in..."
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary mt-6">
                    Join Youth Group
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouthProgramPage;
